import { hasProp } from '../../utils';

import type { MenuProps } from './Menu.types';

type BasicItemOrGroup = {
  id: unknown;
  primary?: unknown;
  items?: { id: unknown; primary?: unknown }[] | undefined;
};

type BasicSelectable = {
  id: unknown;
  primary?: unknown;
  selected?: boolean | undefined;
  items?: { id: unknown; primary?: unknown; selected?: boolean | undefined }[] | undefined;
};

type ExtractItem<T> = Extract<T, { primary: unknown }>;

type GenericItemOrGroup<T> =
  | T
  | (T extends { items?: (infer U)[] | undefined }
      ? U extends { id: unknown }
        ? U
        : never
      : never);

type GenericItem<T> = ExtractItem<T> | ExtractItem<GenericItemOrGroup<T>>;

type PickId<T extends { id: unknown }> = [T] extends [never]
  ? unknown
  : GenericItemOrGroup<T>['id'];

const helpers = {
  isItem<T extends object>(item: T): item is ExtractItem<T> {
    return hasProp(item, 'primary');
  },

  getItem<T extends BasicItemOrGroup>(items: T[], id: PickId<T>): GenericItem<T> | undefined {
    let found: GenericItem<T> | undefined;
    items.some(item => {
      if (this.isItem(item) && item.id === id) {
        found = item;
        return true;
      }

      if (item.items) {
        found = this.getItem(item.items, id);
        return !!found;
      }

      return false;
    });

    return found;
  },

  getPath<T extends BasicItemOrGroup>(items: T[], id: PickId<T>): GenericItemOrGroup<T>[] {
    let path: GenericItemOrGroup<T>[] = [];
    items.some(item => {
      if (item.id === id) {
        path = [item];
        return true;
      }

      if (item.items) {
        const innerPath = this.getPath(item.items as GenericItemOrGroup<T>[], id);
        if (innerPath.length) path = innerPath.concat(item);
        return !!path.length;
      }

      return false;
    });

    return path;
  },

  setItem<T extends BasicItemOrGroup>(
    items: T[],
    id: PickId<T>,
    newItem: GenericItemOrGroup<T>
  ): T[] {
    return items.map(item => {
      if (item.id === id) return { ...newItem };

      if (item.items) {
        return {
          ...item,
          items: this.setItem(item.items, id, newItem)
        };
      }

      return item;
    });
  },

  mapItem<T extends BasicItemOrGroup>(
    items: T[],
    id: PickId<T>,
    fn: (item: ExtractItem<T>, index: number, array: T[]) => ExtractItem<T>
  ): T[] {
    return items.map((item, index, array) => {
      let newItem = item;

      if (item.items) {
        newItem = {
          ...newItem,
          items: this.mapItem(item.items as GenericItemOrGroup<T>[], id, fn)
        };
      }

      if (this.isItem(newItem) && item.id === id) {
        newItem = fn(newItem, index, array);
      }

      return newItem;
    });
  },

  mapTree<T extends BasicItemOrGroup>(
    items: T[],
    fn: (item: ExtractItem<T>, index: number, array: T[]) => ExtractItem<T>
  ): GenericItemOrGroup<T>[] {
    return items.map((item, index, array) => {
      let newItem = item;

      if (item.items) {
        newItem = {
          ...newItem,
          items: this.mapTree(item.items as GenericItemOrGroup<T>[], fn)
        };
      }

      return this.isItem(newItem) ? fn(newItem, index, array) : newItem;
    });
  },

  flatten<T extends BasicItemOrGroup>(
    items: T[],
    ancestors: GenericItemOrGroup<T>[] = [],
    options: { parentFirst?: boolean } = { parentFirst: false }
  ): ExtractItem<T>[] {
    let allItems: ExtractItem<T>[] = [];
    let leafItems: ExtractItem<T>[] = [];

    items.forEach(item => {
      if (this.isItem(item)) {
        allItems.push(ancestors.length > 0 ? { ...item, ancestors } : item);
      }

      if (item.items) {
        const children = this.flatten(item.items, [...ancestors, item], options);

        if (options.parentFirst) {
          leafItems = [...leafItems, ...children];
        } else {
          allItems = [...allItems, ...children];
        }
      }
    });

    return options.parentFirst ? [...allItems, ...leafItems] : allItems;
  },

  toggleSelected<T extends BasicSelectable>(
    items: T[],
    id: PickId<T>,
    mode?: MenuProps['mode'],
    bool?: boolean
  ): T[] {
    return this.mapTree(items, item => {
      if (this.isItem(item)) {
        if (item.id === id) {
          return {
            ...item,
            selected: bool !== undefined ? bool : !item.selected
          };
        }

        if (mode === 'single-select') {
          return {
            ...item,
            selected: false
          };
        }
      }

      return item;
    });
  },

  selectItem<T extends BasicSelectable>(items: T[], id: PickId<T>, mode?: MenuProps['mode']): T[] {
    return this.toggleSelected(items, id, mode, true);
  },

  deselectItem<T extends BasicSelectable>(
    items: T[],
    id: PickId<T>,
    mode?: MenuProps['mode']
  ): T[] {
    return this.toggleSelected(items, id, mode, false);
  },

  getSelected<T extends BasicSelectable>(items: T[]): ExtractItem<T>[] {
    return items.reduce<ExtractItem<T>[]>((selections, item) => {
      if (this.isItem(item) && item.selected) {
        selections = [...selections, item];
      }

      if (item.items) {
        selections = [...selections, ...this.getSelected(item.items)];
      }
      return selections;
    }, []);
  },

  prependTo<T extends BasicItemOrGroup>(
    items: T[],
    newItems: GenericItemOrGroup<T>[],
    id?: PickId<T>
  ): GenericItemOrGroup<T>[] {
    if (id) {
      return this.mapItem(items, id, item => ({
        ...item,
        items: [...newItems, ...(item.items ?? [])]
      }));
    }
    return [...newItems, ...items];
  },

  appendTo<T extends BasicItemOrGroup>(
    items: T[],
    newItems: GenericItemOrGroup<T>[],
    id?: PickId<T>
  ): GenericItemOrGroup<T>[] {
    if (id) {
      return this.mapItem(items, id, item => ({
        ...item,
        items: [...(item.items ?? []), ...newItems]
      }));
    }
    return [...items, ...newItems];
  },

  getNextItem<T extends BasicItemOrGroup>(
    items: T[],
    itemId?: PickId<T>
  ): GenericItemOrGroup<T> | undefined {
    if (!itemId) return items[0];
    let nextItem: GenericItemOrGroup<T> | undefined;
    this.mapItem(items, itemId, (item, index, list) => {
      nextItem = list[index + 1];
      return item;
    });
    return nextItem;
  },

  getPrevItem<T extends BasicItemOrGroup>(
    items: T[],
    itemId?: PickId<T>
  ): GenericItemOrGroup<T> | undefined {
    if (!itemId) return items[0];
    let prevItem: GenericItemOrGroup<T> | undefined;
    this.mapItem(items, itemId, (item, index, list) => {
      prevItem = list[index - 1];
      return item;
    });
    return prevItem;
  },

  getParentItem<T extends BasicItemOrGroup>(
    items: T[],
    itemId?: PickId<T>
  ): GenericItemOrGroup<T> | undefined {
    if (!itemId) return undefined;
    const [, parentItem] = this.getPath(items, itemId);
    return parentItem;
  }
};

export default helpers;
